(ns ui.navigation
  (:require ["lucide-react" :refer [ChevronRight ChevronLeft]]
            [reagent.core :as reagent]
            [ui.skeleton :refer [skeleton]]
            [ui.utils :refer [clsx]]
            [re-posh.core :as re-posh]))

(defn get-active-link-index [links]
  (->> links
       (map-indexed (fn [idx {:keys [active?]}] (when active? idx)))
       (filter identity)
       first))

(defn new-page-badge []
  [:div {:class "text-2xs font-bold px-1 rounded-full bg-gradient-to-r from-indigo-600 to-violet-700"}
    "New"])

(defn menu-link
  [{:keys [name icon href data-cy target disabled active? new? on-click title label class]}]
  [:a {:href     href
       :title    title
       :target   target
       :data-cy  data-cy
       :on-click on-click
       :class    (clsx "relative flex gap-2 py-1 px-2 h-8 items-center font-medium text-kleene-text/75 transition-colors rounded-md cursor-pointer hover:text-kleene-text group active:scale-[0.98]"
                       {"bg-kleene-neutral-02 dark:bg-kleene-selected-background text-kleene-text" active?
                        "pointer-events-none opacity-50" disabled}
                       class)}
   [:> icon {:class "h-5! w-5! shrink-0 mx-1 opacity-75 group-hover:opacity-100" :size 20}]
   [:div {:class "flex flex-1"}
    [:div {:class "truncate"} name]
    (when new?
      [:div {:class "ml-0.5"} [new-page-badge]])
    (when label
      [:div {:class "grow flex justify-end"}
       [:f> label]])]])

(defn menu-section [{:keys [class]} & content]
  (into [:section {:class (clsx "px-1" class)}]
        content))

(defn expandable-section
  [{{:keys [initial-expand]
     :or   {initial-expand true}} :section}]
  (let [expanded? (reagent/atom initial-expand)]
    (fn [{:keys [section on-expand]
          {:keys [title links]} :section}]
      (let [active-item-index (get-active-link-index links)

            toggle-expand (fn []
                            (let [new-state (not @expanded?)]
                              (reset! expanded? new-state)
                              (when on-expand (on-expand section new-state))))]

        [menu-section {}
         [:div {:class    "flex gap-1 uppercase font-semibold mb-1 items-center transition-colors cursor-pointer text-kleene-text/40 text-xs hover:text-kleene-text"
                :on-click toggle-expand}
          [:div {:class (clsx "transition-transform rotate-0"
                              {"rotate-90" @expanded?})}
           [:> ChevronRight]]
          title]

         [:div {:class (clsx "overflow-hidden transition-all origin-top"
                             {"h-0!" (and (not @expanded?) (not active-item-index))
                              "h-8!" (and (not @expanded?) active-item-index)})
                :style (when @expanded?
                         {:height (str (* 32 (count links)) "px")})}
          [:div {:class (clsx "transition-all")
                 :style (when (and (not @expanded?) active-item-index)
                          {:transform (str "translate(0, -" (* active-item-index 32) "px)")})}
           (for [{:keys [name] :as link} links]
             ^{:key name}
             [menu-link link])]]]))))

(defn logo [props]
  (let [custom-logo (some-> @(re-posh/subscribe [:unleash/get-variant "fe.custom-logo"])
                            (get-in [:payload :value])
                            js/JSON.parse
                            js->clj)]
    [:a (merge {:href "/"} props)
     [:img (merge
            {:src    "/img/branding/logo-large-2022.svg"
             :width  70
             :height 16
             :class  "hidden dark:inline-block align-top"}
            custom-logo)]
     [:img (merge
            {:src    "/img/branding/logo-large-light-2022.svg"
             :width  70
             :height 16
             :class  "dark:hidden align-top"}
            custom-logo)]]))

(defn expandable-sections-list [{:keys [sections on-expand]}]
  [:<>
   (for [section sections]
     (if (:links section)
       ^{:key (:title section)}
       [expandable-section {:section   section
                            :on-expand on-expand}]
       ^{:key (:name section)}
       [menu-section {} [menu-link section]]))])



(defn skeleton-menu-node [{:keys [title items]}]
  [:div {:class "flex flex-col gap-2"}
   [skeleton {:class "my-2" :style {:height 14 :width title}}]

   (for [i (range (count items))
         :let [item (nth items i)]]
     [:div {:class "pl-2 flex flex-row gap-2 items-center"
            :key   i}
      [skeleton {:style {:width 16 :height 16}}]
      [skeleton {:style {:width item :height 12}}]])])


(defn skeleton-menu []
  [:div {:class "px-2 flex flex-col gap-4"}
   [skeleton-menu-node {:title "50%" :items ["80%" "60%" "60%"]}]
   [skeleton-menu-node {:title "40%" :items ["70%" "75%" "30%" "40%" "50%"]}]
   [skeleton-menu-node {:title "30%" :items ["30%" "40%" "80%" "30%"]}]
   [skeleton-menu-node {:title "40%" :items ["70%" "80%" "85%"]}]
   [skeleton-menu-node {:title "45%" :items ["60%" "65%"]}]])

(defn christmas-lights []
  (when @(re-posh/subscribe [:unleash/is-enabled "fe.christmas-partey"])
    [:div.christmas-lights
     (for [i (range 9)]
       ^{:key i}
       [:div])]))

(defn sidebar
  [{:keys [initial-expand logo-props]
    :or   {initial-expand true}}]
  (let [expanded? (reagent/atom initial-expand)
        hovered?  (reagent/atom false)
        timeout   (atom nil)]
    (fn [{:keys [post-header post-nav class on-expand bg-class loading?]} children]
      (let [toggle-expand (fn []
                            (let [new-state (not @expanded?)]
                              (reset! expanded? new-state)
                              (when (and @hovered? (not new-state)) (reset! hovered? false))
                              (when on-expand (on-expand new-state))))
            on-mouse-enter (fn [e]
                             (when-not (some-> e .-target .-classList (.contains "close-button"))
                               (reset! timeout (js/setTimeout #(reset! hovered? true) 300))))
            on-mouse-leave (fn []
                             (when @timeout
                               (js/clearTimeout @timeout)
                               (reset! timeout nil)
                               (reset! hovered? false)))]

        [:aside {:class (clsx "bg-kleene-neutral-04 dark:bg-kleene-background transition-all duration-300 print:hidden z-10 text-kleene-text"
                              {"w-48" @expanded?
                               "w-4"  (not @expanded?)
                               "drop-shadow-2xl" (and (not @expanded?) @hovered?)
                               "z-20" @hovered?}
                              class
                              bg-class)}
         [:div {:class (clsx "sticky top-0 h-screen transition-all duration-300 group/sidebar"
                             "border-r border-kleene-layout-border"
                             {"w-48" (or @hovered? @expanded?)
                              "w-4"  (and (not @hovered?) (not @expanded?))})
                :on-mouse-enter on-mouse-enter
                :on-mouse-leave on-mouse-leave}
          [:div {:class (clsx "h-full overflow-hidden bg-kleene-neutral-04 dark:bg-kleene-background transition-all"
                              {"opacity-0 delay-300 hover:opacity-100" (and (not @hovered?) (not @expanded?))}
                              bg-class)}
           [:div {:class "w-48 flex flex-col max-h-screen h-full"}
            [:div {:class "relative flex flex-col gap-3 p-4 border-b border-kleene-layout-border logo-bar"}
             [christmas-lights]
             [logo (update logo-props :class #(clsx "h-8 flex flex-col justify-center" %))]
             post-header]

            [:nav {:class "flex-1 flex flex-col gap-4 pb-2 overflow overflow-y-auto scrollbar"}
             (if loading?
               [skeleton-menu]
               children)]

            (when post-nav
              [:div {:class "p-4 border-t border-kleene-layout-border"}
               post-nav])]]

          [:div {:class (clsx "close-button absolute bottom-3 p-2 -right-1 translate-x-1/2 z-10 hidden group-hover/sidebar:block"
                              {"block!" (not @expanded?)})}
           [:div {:class "w-8 h-8 rounded-full transition-colors flex items-center justify-center cursor-pointer shadow-lg bg-kleene-selected-background"
                  :on-click toggle-expand}
            [:> ChevronLeft {:class (clsx "transition-transform" {"rotate-180" (not @expanded?)})}]]]]]))))
